// src/context/CountContext.js
import React, { createContext, useReducer } from 'react';
// Template for report values
const initialReport = {
  creditTips: 0,
  totalCharges: 0,
  giftCardPayment: 0,
  totalSales: 0,
  totalMinusGift: 0,
  totalTax: 0,
  giftCardSold: 0
};
// Initial application state
const initialState = {
  preCount: { store: '', date: new Date().toISOString().split('T')[0], shift: 'morning', startingBalance: 0, manager: '' },
  user: {
    name: '',
    store: ''
  },
  rolls: { quarters: 0, dimes: 0, nickels: 0, pennies: 0 },
  writtenTipConfirmed: false,
  // your live report values
  report: { ...initialReport },

  // snapshot of the report *before* any ApplyAdjustment
  originalReport: { ...initialReport },

  tipBucket: { coins: 0, bills: 0, removal: 0 },
  denominations: { '100': 0, '50': 0, '20': 0, '10': 0, '5': 0, '1': 0, '0.25': 0, '0.10': 0, '0.05': 0, '0.01': 0 },
  drawerCoins: 0,
  bills: { ones: 0, fives: 0, tens: 0, twenties: 0, fifties: 0, hundreds: 0 },
  thresholds: { discrepancy: 5, tipRounding: 0.85, starterCash: 500 },
  tax: { salesRate: 0.0975, tipShareRate: 0 },
  overage: 0,
  vendorPayouts: [],
  overageApplied: false,
  salesAdjusted: false,
  reportAdjusted: false,
  taxConfirmed: false,
  reportSent: false


};

// Reducer function
function countReducer(state, action) {
  switch (action.type) {
    case 'UPDATE_DENOMINATION': {
      const { denomination, count } = action.payload;
      return {
        ...state,
        denominations: {
          ...state.denominations,
          [denomination]: count
        }
      };
    }

    case 'UPDATE_ROLLS': {
      const { quarters, dimes, nickels, pennies } = action.payload;
      return { ...state, rolls: { quarters, dimes, nickels, pennies } };
    }

    case 'CONFIRM_WRITTEN_TIP':
      return { ...state, writtenTipConfirmed: action.payload };

    case 'UPDATE_TIP_BUCKET_COINS':
      return {
        ...state,
        tipBucket: { ...state.tipBucket, coins: action.payload }
      };

    case 'UPDATE_TIP_BUCKET_BILLS':
      return {
        ...state,
        tipBucket: { ...state.tipBucket, bills: action.payload }
      };

    case 'SET_TIP_REMOVAL':
      return {
        ...state,
        tipBucket: { ...state.tipBucket, removal: action.payload }
      };

    case 'UPDATE_DRAWER_COINS':
      return { ...state, drawerCoins: action.payload };

    case 'UPDATE_BILL': {
      const { billType, count } = action.payload;
      return {
        ...state,
        bills: { ...state.bills, [billType]: count }
      };
    }

    case 'UPDATE_REPORT_FIELD': {
      const { field, value } = action.payload;
      return {
        ...state,
        report: { ...state.report, [field]: value }
      };
    }

    case 'SET_VENDOR_PAYOUTS':
      return { ...state, vendorPayouts: action.payload };

    case 'CONFIRM_TAX_BREAKDOWN':
      return { ...state, taxConfirmed: true };

    case 'SEND_REPORT':
      return { ...state, reportSent: true };

    case 'APPLY_OVERAGE': {
      const over = action.payload;
      return {
        ...state,
        overage: over,
        overageApplied: true,
        tipBucket: {
          ...state.tipBucket,
          bills: state.tipBucket.bills + over
        }
      };
    }

    case 'APPLY_REPORT_ADJUSTMENT': {
      const { adjustedTotal, adjustedTax } = action.payload;
      return {
        ...state,
        originalReport: { ...state.report },       // ← stash the old values
        report: {
          ...state.report,
          totalSales: adjustedTotal,
          totalMinusGift: adjustedTotal,
          totalTax: adjustedTax
        },
        reportAdjusted: true,
        salesAdjusted: true
      };
    }
    case 'UPDATE_PRECOUNT_FIELD': {
      const { field, value } = action.payload;
      return {
        ...state,
        preCount: {
          ...state.preCount,
          [field]: value
        }
      };
    }
    case 'UNDO_REPORT_ADJUSTMENT': {
      // pull the original values back out
      const { totalSales, totalMinusGift, totalTax } = state.originalReport;
      return {
        ...state,
        report: {
          ...state.report,
          totalSales,
          totalMinusGift,
          totalTax
        },
        reportAdjusted: false,
        salesAdjusted: false
      };
    }
    case 'SET_USER': {
      // payload is expected to be an object like { name: string, store: string }
      return {
        ...state,
        user: action.payload
      };
    }

    default:
      return state;
  }
}


// Context creation
export const CountContext = createContext({ state: initialState, dispatch: () => { } });

// Provider component
export function CountProvider({ children }) {
  const [state, dispatch] = useReducer(countReducer, initialState);
  return <CountContext.Provider value={{ state, dispatch }}>{children}</CountContext.Provider>;
}

export default CountContext;
